/**
 * Quotes API endpoints
 *
 * This module contains all API endpoint functions related to quotes.
 */

import { apiClient } from './client';
import type { AddressAccessCondition } from '@/lib/constants/address-access';

// ==================== Types ====================

export type QuoteStatus =
  | 'DRAFT'
  | 'IN_REVIEW'
  | 'BOOKING_IN_REVIEW'
  | 'WAITING_CLIENT_CONFIRMATION'
  | 'READY_TO_BOOK'
  | 'COMPLETED'
  | 'REJECTED'
  | 'CANCELLED'
;

export type ComponentType = 'LINEHAUL' | 'PICKUP' | 'DELIVERY' | 'TAILGATE' | 'FUEL_SURCHARGE' | 'CUSTOM_SERVICE' | 'CROSSDOCK_INBOUND' | 'CROSSDOCK_OUTBOUND';
export type CargoSourceType = 'PACKAGE' | 'CARGO' | 'ITEM';
export type ScheduleTimeType = 'FIXED' | 'FLEXIBLE';
export type ScheduleDateType = 'FIXED' | 'FLEXIBLE';

export interface QuoteAddress {
  organizationAddressId?: string | null;
  addressId?: string | null;
  locationName?: string | null;
  locationType?: 'business' | 'residential';
  addressLine1?: string | null;
  addressLine2?: string | null;
  city: string;
  state?: string | null;
  postalCode: string;
  country: string;
  contactName?: string | null;
  contactPhone?: string | null;
  contactEmail?: string | null;
  accessConditions?: AddressAccessCondition[];
  accessNotes?: string | null;
}

export interface QuoteSchedule {
  quoteId: string;
  // Pickup date fields
  pickupDateType: ScheduleDateType | null;
  pickupDateFrom: string | null;
  pickupDateTo: string | null;
  // Pickup time fields
  pickupTimeType: ScheduleTimeType | null;
  pickupTimeFrom: string | null;
  pickupTimeTo: string | null;
  // Delivery date fields
  deliveryDateType: ScheduleDateType | null;
  deliveryDateFrom: string | null;
  deliveryDateTo: string | null;
  // Delivery time fields
  deliveryTimeType: ScheduleTimeType | null;
  deliveryTimeFrom: string | null;
  deliveryTimeTo: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface QuoteOrigin {
  quoteId: string;
  organizationAddressId: string | null;
  addressId?: string | null;
  locationName: string | null;
  locationType: string | null;
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  state: string | null;
  postalCode: string;
  country: string;
  contactName: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
  tailgateRequired?: boolean | null;
  accessConditions?: AddressAccessCondition[];
  accessNotes?: string | null;
  accessFromQuoteSnapshot?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface QuoteDestination {
  quoteId: string;
  organizationAddressId: string | null;
  addressId?: string | null;
  locationName: string | null;
  locationType: string | null;
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  state: string | null;
  postalCode: string;
  country: string;
  contactName: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
  tailgateRequired?: boolean | null;
  accessConditions?: AddressAccessCondition[];
  accessNotes?: string | null;
  accessFromQuoteSnapshot?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface QuoteCargoItem {
  id: string;
  quoteId: string;
  sourceType: CargoSourceType;
  itemId?: string | null;
  description: string;
  quantity: number;
  length: string;
  width: string;
  height: string;
  weight: string;
  dimensionUnit: 'cm' | 'mm';
  weightUnit: 'kg';
  isStackable: boolean;
  palletSpaces: string;
  cbm?: string | null;
  createdAt: string;
  // Item details (available when sourceType is 'ITEM')
  itemName?: string | null;
  itemImages?: string[] | null;
}

export interface QuoteCostComponent {
  id: string;
  quoteId: string;
  componentType: ComponentType;
  unitType?: QuoteCostingMethod | null;
  costGroup?: string | null;
  description?: string | null;
  serviceType?: string | null;
  ratePerUnit?: string | null;
  unitValue?: string | null;
  baseCost: string;
  marginPercentage: string;
  marginAmount: string;
  finalCost: string;
  createdAt: string;
  isPlaceholder?: boolean;
}

export interface QuoteActivity {
  id: string;
  quoteId: string;
  action: string;
  fieldChanged?: string | null;
  oldValue?: string | null;
  newValue?: string | null;
  notes?: string | null;
  performedById: string;
  performedBy?: {
    id: string;
    name: string;
    avatarUrl: string | null;
  } | null;
  attachmentUrl?: string | null;
  attachmentName?: string | null;
  attachmentMimeType?: string | null;
  attachmentSizeBytes?: number | null;
  createdAt: string;
}

export interface QuoteComment {
  id: string;
  quoteId: string;
  message: string;
  performedBy: {
    id: string;
    name: string;
  } | null;
  attachmentUrl?: string | null;
  attachmentName?: string | null;
  attachmentMimeType?: string | null;
  attachmentSizeBytes?: number | null;
  createdAt: string;
}

/**
 * Organization activity feed item (for dashboard activity feed)
 */
export interface OrganizationActivity {
  id: string;
  quoteId: string;
  quoteNumber: string;
  type: 'created' | 'updated' | 'submitted' | 'approved' | 'rejected' | 'booked' | 'completed';
  title: string;
  description: string | null;
  performedBy: {
    id: string;
    name: string;
  } | null;
  createdAt: string;
}

/**
 * Quote summary for dashboard KPIs
 */
export interface QuoteSummary {
  statusCounts: Record<QuoteStatus, number>;
  totalQuotes: number;
}

/**
 * Organization margin settings for quote
 */
export interface QuoteOrganizationMargins {
  pickupMarginPercentage: number;
  linehaulMarginPercentage: number;
  deliveryMarginPercentage: number;
  tailgateMarginPercentage: number;
}

export type QuoteCostingMethod = 'PALLET' | 'CBM';
export type DeliveryAccessCondition =
  | 'LOADING_DOCK'
  | 'GROUND_LEVEL'
  | 'REAR_LANE'
  | 'HEIGHT_RESTRICTED'
  | 'NOT_AVAILABLE'
  | 'OTHER';

export interface Quote {
  id: string;
  referenceNumber: string;
  organizationId: string;
  createdById: string;
  status: QuoteStatus;
  isPoa: boolean;
  /** Snapshot at quote creation; null on legacy quotes (pallet edit disabled). */
  costingMethod?: QuoteCostingMethod | null;


  // Other
  specialInstructions: string | null;
  deliveryAccessCondition: DeliveryAccessCondition | null;
  deliveryAccessValue: string | null;
  orderReferenceNumber?: string | null;
  /** Optional purchase order document (signed URL when fetched via getQuote). */
  poFileUrl?: string | null;
  poFileName?: string | null;
  /** Carrier consignment reference; set when the job is completed. */
  connoteNumberReference?: string | null;
  /** Operations-confirmed fixed pickup date & time (ISO 8601 UTC); set at Confirm Booking (STD-590). */
  fixedPickupAt?: string | null;
  /** Operations-confirmed fixed delivery date & time (ISO 8601 UTC); set at Confirm Booking (STD-590). */
  fixedDeliveryAt?: string | null;

  // Totals
  totalBaseCost: string;
  totalMarginAmount: string;
  totalFuelSurcharge: string;
  totalSellPrice: string;

  // Organization margins
  organization: QuoteOrganizationMargins | null;

  createdAt: string;
  updatedAt: string;
}

export interface QuoteWithDetails extends Quote {
  origin: QuoteOrigin | null;
  destination: QuoteDestination | null;
  schedule: QuoteSchedule | null;
  cargoItems: QuoteCargoItem[];
  costComponents: QuoteCostComponent[];
  activities: QuoteActivity[];
}

// ==================== Input Types ====================

export interface QuoteCargoItemInput {
  sourceType: CargoSourceType;
  itemId?: string | null;
  description?: string | null;
  quantity: number;
  length: number;
  width: number;
  height: number;
  weight: number;
  dimensionUnit: 'cm' | 'mm';
  weightUnit: 'kg';
  isStackable?: boolean;
  /**
   * Optional override used by PALLET costing to persist a manually edited pallet space total.
   * If omitted, the API will calculate pallet spaces from dimensions/formula.
   */
  calculatedPalletSpaces?: number;
  /** Optional override for CBM costing, if needed in future edits. */
  calculatedCbm?: number;
}

export interface QuoteScheduleInput {
  // Pickup date fields
  pickupDateType?: ScheduleDateType;
  pickupDateFrom?: string | null;
  pickupDateTo?: string | null;
  // Pickup time fields
  pickupTimeType?: ScheduleTimeType;
  pickupTimeFrom?: string;
  pickupTimeTo?: string;
  // Delivery date fields
  deliveryDateType?: ScheduleDateType;
  deliveryDateFrom?: string | null;
  deliveryDateTo?: string | null;
  // Delivery time fields
  deliveryTimeType?: ScheduleTimeType;
  deliveryTimeFrom?: string;
  deliveryTimeTo?: string;
}

export interface AdditionalService {
  tailgate?: boolean;
}

export interface CreateQuoteRequest {
  organizationId: string;
  origin: QuoteAddress;
  destination: QuoteAddress;
  cargoItems: QuoteCargoItemInput[];
  schedule?: QuoteScheduleInput;
  additionalService?: AdditionalService;
  specialInstructions?: string | null;
  deliveryAccessCondition?: DeliveryAccessCondition | null;
  deliveryAccessValue?: string | null;
  status?: 'DRAFT' | 'IN_REVIEW' | 'BOOKING_IN_REVIEW';
  orderReferenceNumber?: string;
}

export interface UpdateQuoteRequest {
  origin?: Partial<QuoteAddress>;
  destination?: Partial<QuoteAddress>;
  cargoItems?: QuoteCargoItemInput[];
  schedule?: QuoteScheduleInput;
  additionalService?: AdditionalService;
  specialInstructions?: string | null;
  deliveryAccessCondition?: DeliveryAccessCondition | null;
  deliveryAccessValue?: string | null;
}

export interface ScheduleDetail {
  scheduleType: 'flexible' | 'specific';
  dateFrom?: string;
  dateTo?: string;
  timeFrom?: string;
  timeTo?: string;
  date?: string;
  time?: string;
}

export interface CalculatePricingRequest {
  /** Existing quote id — uses stored costing method when set (legacy null uses org). */
  quoteId?: string;
  organizationId: string;
  origin: {
    addressId?: string;
    postalCode: string;
    state?: string;
    zoneCode?: string;
  };
  destination: {
    addressId?: string;
    postalCode: string;
    state?: string;
    zoneCode?: string;
  };
  cargoItems: QuoteCargoItemInput[];
  pickupSchedule?: ScheduleDetail;
  deliverySchedule?: ScheduleDetail;
  tailgateRequired?: boolean;
  crossdockInboundRequired?: boolean;
  crossdockOutboundRequired?: boolean;
  /**
   * Optional per-component pinned values. When set, the BE skips its
   * calculator for that component and uses these values verbatim. Fuel
   * surcharge is always recomputed by the BE from the resulting subtotal.
   */
  overrides?: {
    pickup?:   { baseCost: number; marginAmount: number; finalCost: number };
    linehaul?: { baseCost: number; marginAmount: number; finalCost: number };
    delivery?: { baseCost: number; marginAmount: number; finalCost: number };
    tailgate?: { baseCost: number; marginAmount: number; finalCost: number };
  };
}

export interface PricingCalculation {
  isPoa: boolean;
  cargoItems: Array<{
    description: string;
    quantity: number;
    palletSpaces: number;
  }>;
  costComponents: Array<{
    componentType: ComponentType;
    description?: string;
    baseCost: number;
    marginPercentage: number;
    marginAmount: number;
    finalCost: number;
  }>;
  totalBaseCost: number;
  totalMarginAmount: number;
  totalSellPrice: number;
}

/**
 * Pickup cost breakdown item
 */
export interface PickupCostBreakdownItem {
  item_index: number;
  dimensions_cm: {
    length: number;
    width: number;
    height: number;
  };
  quantity: number;
  item_volume_cm3: number;
  pallet_volume_cm3: number;
  pallet_spaces_per_unit: number;
  total_pallet_spaces: number;
  total_cbm?: number;
  rate_used: number;
  item_base_cost: number;
}

/**
 * Pickup cost calculation result
 */
export interface PickupCostResult {
  zone: string;
  rate_type: 'standard' | 'urgent';
  cost_group: string | null;
  cbm_band_id?: string | null;
  total_pallet_spaces: number;
  total_base_cost: number;
  margin_amount: number;
  fuel_surcharge: number;
  final_pickup_charge: number;
  breakdown: PickupCostBreakdownItem[];
  message: string | null;
}

/**
 * Delivery cost breakdown item
 */
export interface DeliveryCostBreakdownItem {
  item_index: number;
  dimensions_cm: {
    length: number;
    width: number;
    height: number;
  };
  quantity: number;
  item_volume_cm3: number;
  pallet_volume_cm3: number;
  pallet_spaces_per_unit: number;
  total_pallet_spaces: number;
  total_cbm?: number;
  rate_used: number;
  item_base_cost: number;
}

/**
 * Delivery cost calculation result
 */
export interface DeliveryCostResult {
  zone: string;
  rate_type: 'standard' | 'urgent';
  cost_group: string | null;
  cbm_band_id?: string | null;
  total_pallet_spaces: number;
  total_base_cost: number;
  margin_amount: number;
  fuel_surcharge: number;
  final_delivery_charge: number;
  breakdown: DeliveryCostBreakdownItem[];
  message: string | null;
}

/**
 * Tailgate cost calculation result
 */
export interface TailgateCostResult {
  pallet_spaces: number;
  total_cbm?: number;
  cost_group: string | null;
  cbm_band_id?: string | null;
  rate?: number;
  base_cost: number;
  margin_amount: number;
  min_charge_applied: boolean;
  total_charge: number;
  message: string | null;
}

export interface CrossdockCostResult {
  crossdock_type: 'INBOUND' | 'OUTBOUND';
  total_cbm: number;
  cbm_band_id: string;
  rate: number;
  base_cost: number;
  margin_percentage: number;
  margin_amount: number;
  min_charge: number;
  min_charge_applied: boolean;
  total_charge: number;
  message: string | null;
  is_poa: boolean;
}

/**
 * Linehaul cost breakdown item
 */
export interface LinehaulCostBreakdownItem {
  item_index: number;
  dimensions_cm: {
    length: number;
    width: number;
    height: number;
  };
  quantity: number;
  item_volume_cm3: number;
  pallet_volume_cm3: number;
  pallet_spaces_per_unit: number;
  total_pallet_spaces: number;
  total_cbm?: number;
  rate_used: number;
  item_base_cost: number;
}

/**
 * Linehaul cost calculation result
 */
export interface LinehaulCostResult {
  origin_state: string;
  destination_state: string;
  rate_type: 'standard' | 'urgent';
  total_pallet_spaces: number;
  rate_per_pallet: number;
  total_base_cost: number;
  margin_amount: number;
  final_linehaul_charge: number;
  transit_time_hours: number;
  breakdown: LinehaulCostBreakdownItem[];
  message: string | null;
  is_na: boolean;
  is_poa: boolean;
}

/**
 * New pricing calculation response
 */
export interface FuelSurchargeCalculationResult {
  percentage: number;
  amount: number;
}



export interface PricingCalculationResponse {
  pickup_cost: PickupCostResult | null;
  linehaul_cost: LinehaulCostResult | null;
  delivery_cost: DeliveryCostResult | null;

  additional_services_cost: {
    tailgate: TailgateCostResult | null;
    crossdock_inbound: CrossdockCostResult | null;
    crossdock_outbound: CrossdockCostResult | null;
  };
  /** Present on API pricing responses; includes resolved % used for the fuel line. */
  fuel_surcharge?: FuelSurchargeCalculationResult | null;
  summary?: {
    fuel_surcharge: number;
    subtotal: number;
    grand_total: number;
  };
  message?: string; // POA message
}

export interface UpdateQuotePricingRequest {
  costComponents: Array<{
    componentType: ComponentType;
    description?: string;
    serviceType?: string;
    ratePerUnit?: number;
    unitType?: QuoteCostingMethod | null;
    unitValue?: number | null;
    costGroup?: string | null;
    baseCost: number;
    marginPercentage: number;
    marginAmount: number;
    finalCost: number;
  }>;
  isPoa?: boolean;
}

export interface SubmitReviewedRequest extends Partial<UpdateQuotePricingRequest> {
  /**
   * STD-529: optional Sales/GM "Message for client" persisted to
   * `client_facing_message` on the WAITING_CLIENT_CONFIRMATION transition for
   * display on the internal quote detail page. Not included in the client email.
   */
  clientFacingMessage?: string | null;
  /**
   * Optional schedule updates that will be persisted when the quote is submitted
   * for client approval (or ready-to-book for booking review).
   */
  schedule?: Partial<Pick<
    QuoteSchedule,
    | 'pickupDateType'
    | 'pickupDateFrom'
    | 'pickupDateTo'
    | 'pickupTimeType'
    | 'pickupTimeFrom'
    | 'pickupTimeTo'
    | 'deliveryDateType'
    | 'deliveryDateFrom'
    | 'deliveryDateTo'
    | 'deliveryTimeType'
    | 'deliveryTimeFrom'
    | 'deliveryTimeTo'
  >>;
}

// ==================== Query Params ====================

export interface QuotesListParams {
  page?: number;
  limit?: number;
  status?: string; // Changed from QuoteStatus to string for multi-status support
  organizationId?: string;
  search?: string;
  /** Case-insensitive "contains" filter on connote number. */
  connoteNumberReference?: string;
  sortBy?: 'createdAt' | 'pickupDate' | 'totalSellPrice' | 'connoteNumberReference';
  sortOrder?: 'asc' | 'desc';
  // Date range filters
  pickupDateFrom?: string;
  pickupDateTo?: string;
  deliveryDateFrom?: string;
  deliveryDateTo?: string;
}

export interface PaginationMeta {
  page: number;
  limit: number;
  totalItems: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface QuotesListResponse {
  quotes: QuoteWithDetails[];
  pagination: PaginationMeta;
}

// ==================== Client API Functions ====================

/**
 * Create a new quote
 */
export async function createQuote(data: CreateQuoteRequest): Promise<{ quote: Quote; pricing: PricingCalculation }> {
  const res = await apiClient.post<{ quote: Quote; pricing: PricingCalculation }>('/api/quotes', data);
  return { ...res, quote: mapQuoteBoundary(res.quote) };
}

/**
 * Get a single quote by ID
 */
export async function getQuote(id: string): Promise<QuoteWithDetails> {
  const quote = await apiClient.get<QuoteWithDetails>(`/api/quotes/${id}`);
  return mapQuoteBoundary(quote);
}

/**
 * List quotes for an organization
 */
export async function listQuotes(organizationId: string, params?: Omit<QuotesListParams, 'organizationId'>): Promise<QuotesListResponse> {
  const response = await apiClient.get<{
    success: boolean;
    data: QuoteWithDetails[];
    meta: {
      pagination: {
        page: number;
        limit: number;
        total_items: number;
        total_pages: number;
        has_next: boolean;
        has_prev: boolean;
      };
    };
  }>(`/api/organizations/${organizationId}/quotes`, { params, unwrapData: false });

  // API returns { success: true, data: [...quotes], meta: { pagination: {...} } }
  // Transform snake_case pagination to camelCase for frontend
  const pagination = response.meta?.pagination;
  return {
    quotes: (response.data || []).map(mapQuoteBoundary),
    pagination: {
      page: pagination?.page ?? 1,
      limit: pagination?.limit ?? 10,
      totalItems: pagination?.total_items ?? 0,
      totalPages: pagination?.total_pages ?? 1,
      hasNext: pagination?.has_next ?? false,
      hasPrev: pagination?.has_prev ?? false,
    },
  };
}

/**
 * Update a quote
 */
export async function updateQuote(id: string, data: UpdateQuoteRequest): Promise<{ quote: Quote; pricing?: PricingCalculation }> {
  const res = await apiClient.put<{ quote: Quote; pricing?: PricingCalculation }>(`/api/quotes/${id}`, data);
  return { ...res, quote: mapQuoteBoundary(res.quote) };
}

/**
 * Update quote addresses only (does not recalculate pricing)
 */
export async function updateQuoteAddress(
  id: string,
  data: {
    origin?: { addressLine1?: string | null; locationName?: string | null };
    destination?: { addressLine1?: string | null; locationName?: string | null };
  },
): Promise<{ quote: Quote }> {
  const res = await apiClient.patch<{ quote: Quote }>(`/api/quotes/${id}/address`, data);
  return { ...res, quote: mapQuoteBoundary(res.quote) };
}

/**
 * Delete a quote (DRAFT only)
 */
export async function deleteQuote(id: string): Promise<void> {
  return apiClient.delete(`/api/quotes/${id}`);
}

/**
 * Calculate pricing preview (without creating a quote)
 */
export async function calculatePricing(data: CalculatePricingRequest): Promise<PricingCalculationResponse> {
  return apiClient.post('/api/quotes/calculate-pricing', data);
}

export interface GetResolvedFuelSurchargePercentageParams {
  organizationId?: string;
  originState: string;
  destinationState: string;
}

/**
 * Resolve fuel surcharge %: organization override (date-bounded) → platform route → global default.
 */
export async function getResolvedFuelSurchargePercentage(
  params: GetResolvedFuelSurchargePercentageParams,
): Promise<number> {
  const data = await apiClient.get<{ percentage: number }>('/api/quotes/fuel-surcharge-percentage', {
    params: {
      originState: params.originState,
      destinationState: params.destinationState,
      ...(params.organizationId ? { organizationId: params.organizationId } : {}),
    },
  });
  return data.percentage;
}

/**
 * Submit quote for review
 */
export async function submitForReview(id: string): Promise<Quote> {
  const quote = await apiClient.post<Quote>(`/api/quotes/${id}/submit-for-review`);
  return mapQuoteBoundary(quote);
}

function buildOrderReferenceFormData(orderReferenceNumber: string, poFile: File): FormData {
  const formData = new FormData();
  formData.append('orderReferenceNumber', orderReferenceNumber);
  formData.append('poFile', poFile);
  return formData;
}

/**
 * Direct book a quote
 */
export async function directBook(
  id: string,
  orderReferenceNumber: string,
  poFile?: File | null,
): Promise<Quote> {
  let quote: Quote;
  if (poFile) {
    quote = await apiClient.postFormData<Quote>(
      `/api/quotes/${id}/direct-book`,
      buildOrderReferenceFormData(orderReferenceNumber, poFile),
    );
  } else {
    quote = await apiClient.post<Quote>(`/api/quotes/${id}/direct-book`, { orderReferenceNumber });
  }
  return mapQuoteBoundary(quote);
}

/**
 * Approve a reviewed quote (client)
 */
export async function approveQuote(
  id: string,
  orderReferenceNumber: string,
  poFile?: File | null,
): Promise<Quote> {
  let quote: Quote;
  if (poFile) {
    quote = await apiClient.postFormData<Quote>(
      `/api/quotes/${id}/approve`,
      buildOrderReferenceFormData(orderReferenceNumber, poFile),
    );
  } else {
    quote = await apiClient.post<Quote>(`/api/quotes/${id}/approve`, { orderReferenceNumber });
  }
  return mapQuoteBoundary(quote);
}

/**
 * Upload PO file for an existing quote (e.g. after wizard create)
 */
export async function uploadQuotePoFile(id: string, file: File): Promise<Quote> {
  const formData = new FormData();
  formData.append('poFile', file);
  const quote = await apiClient.postFormData<Quote>(`/api/quotes/${id}/po-file`, formData);
  return mapQuoteBoundary(quote);
}

/**
 * Reject a quote (client)
 */
export async function rejectQuote(id: string, reason?: string): Promise<Quote> {
  const quote = await apiClient.post<Quote>(`/api/quotes/${id}/reject`, { reason });
  return mapQuoteBoundary(quote);
}

// ==================== Operations API Functions ====================

/**
 * List all quotes (operations)
 */
export async function listAllQuotes(params?: QuotesListParams): Promise<QuotesListResponse> {
  const response = await apiClient.get<{
    success: boolean;
    data: QuoteWithDetails[];
    meta: {
      pagination: {
        page: number;
        limit: number;
        total_items: number;
        total_pages: number;
        has_next: boolean;
        has_prev: boolean;
      };
    };
  }>('/api/admin/quotes', { params: params as Record<string, string | number | boolean | undefined>, unwrapData: false });

  // API returns { success: true, data: [...quotes], meta: { pagination: {...} } }
  // Transform snake_case pagination to camelCase for frontend
  const pagination = response.meta?.pagination;
  return {
    quotes: (response.data || []).map(mapQuoteBoundary),
    pagination: {
      page: pagination?.page ?? 1,
      limit: pagination?.limit ?? 10,
      totalItems: pagination?.total_items ?? 0,
      totalPages: pagination?.total_pages ?? 1,
      hasNext: pagination?.has_next ?? false,
      hasPrev: pagination?.has_prev ?? false,
    },
  };
}

/**
 * Update quote pricing (operations)
 */
export async function updateQuotePricing(id: string, data: UpdateQuotePricingRequest): Promise<{ quote: Quote }> {
  const res = await apiClient.put<{ quote: Quote }>(`/api/admin/quotes/${id}/pricing`, data);
  return { ...res, quote: mapQuoteBoundary(res.quote) };
}

/**
 * Submit reviewed quote (operations)
 */
export async function submitReviewed(id: string, data?: SubmitReviewedRequest): Promise<Quote> {
  const quote = await apiClient.post<Quote>(`/api/admin/quotes/${id}/submit-reviewed`, data);
  return mapQuoteBoundary(quote);
}

/**
 * Approve direct booking (operations)
 */
export async function approveDirectBook(
  id: string,
  orderReferenceNumber: string,
  poFile?: File | null,
): Promise<Quote> {
  let quote: Quote;
  if (poFile) {
    quote = await apiClient.postFormData<Quote>(
      `/api/admin/quotes/${id}/approve-direct-book`,
      buildOrderReferenceFormData(orderReferenceNumber, poFile),
    );
  } else {
    quote = await apiClient.post<Quote>(`/api/admin/quotes/${id}/approve-direct-book`, { orderReferenceNumber });
  }
  return mapQuoteBoundary(quote);
}

/**
 * Payload for the Confirm Booking step (STD-590). Datetimes are ISO 8601 UTC
 * strings (`Date.toISOString()`).
 */
export interface CompleteBookingInput {
  connoteNumberReference: string;
  fixedPickupAt: string;
  fixedDeliveryAt: string;
}

/**
 * Confirm Booking for a READY_TO_BOOK quote (STD-590): records the TMS Job
 * Number plus the fixed pickup/delivery date & time and transitions the quote
 * to COMPLETED in one call.
 */
export async function completeQuote(id: string, input: CompleteBookingInput): Promise<Quote> {
  const quote = await apiClient.post<Quote>(`/api/admin/quotes/${id}/complete`, input);
  return mapQuoteBoundary(quote);
}

/**
 * Partial edit to a booked (COMPLETED) quote (STD-591): the Job Reference
 * Number, the fixed pickup/delivery schedule, or both. Datetimes are ISO 8601
 * UTC strings. The two fixed datetimes must be supplied together.
 */
export interface UpdateBookingInput {
  connoteNumberReference?: string;
  fixedPickupAt?: string;
  fixedDeliveryAt?: string;
}

export async function updateBooking(id: string, input: UpdateBookingInput): Promise<Quote> {
  const quote = await apiClient.patch<Quote>(`/api/admin/quotes/${id}/booking`, input);
  return mapQuoteBoundary(quote);
}

/**
 * Cancel a quote (operations)
 */
export async function cancelQuote(id: string, reason?: string): Promise<Quote> {
  const quote = await apiClient.post<Quote>(`/api/admin/quotes/${id}/cancel`, { reason });
  return mapQuoteBoundary(quote);
}

/**
 * Revive a cancelled quote (operations)
 */
export async function reviveQuote(id: string): Promise<Quote> {
  const quote = await apiClient.post<Quote>(`/api/admin/quotes/${id}/revive`);
  return mapQuoteBoundary(quote);
}

// ==================== Activity Feed API Functions ====================

/**
 * Get organization activity feed
 */
export async function getOrganizationActivities(
  organizationId: string,
  limit?: number
): Promise<OrganizationActivity[]> {
  const params = new URLSearchParams();
  if (limit) {
    params.append('limit', limit.toString());
  }

  const queryString = params.toString();
  const url = `/api/organizations/${organizationId}/activities${queryString ? `?${queryString}` : ''}`;

  return apiClient.get(url);
}

/**
 * Get quote summary for dashboard KPIs
 */
export async function getQuoteSummary(
  organizationId: string
): Promise<QuoteSummary> {
  const url = `/api/organizations/${organizationId}/quotes/summary`;
  return apiClient.get(url);
}

/**
 * Create a comment on a quote
 */
export async function createQuoteComment(
  quoteId: string,
  message: string,
  file?: File | null,
): Promise<QuoteComment> {
  if (file) {
    const formData = new FormData();
    formData.append('message', message);
    formData.append('attachment', file);
    return apiClient.postFormData(`/api/quotes/${quoteId}/comments`, formData);
  }
  return apiClient.post(`/api/quotes/${quoteId}/comments`, { message });
}

/**
 * Create an internal comment on a quote
 */
export async function createQuoteInternalComment(quoteId: string, message: string): Promise<QuoteComment> {
  return apiClient.post(`/api/quotes/${quoteId}/internal-comments`, { message });
}

/**
 * Add a custom service component to a quote (admin / operations)
 */
export async function addCustomService(
  id: string,
  data: { name: string; baseCost: number; markupPercentage: number }
): Promise<{ quote: Quote }> {
  const res = await apiClient.post<{ quote: Quote }>(`/api/admin/quotes/${id}/custom-services`, data);
  return { quote: mapQuoteBoundary(res.quote) };
}

/**
 * Update a custom service component (admin / operations)
 */
export async function updateCustomService(
  id: string,
  componentId: string,
  data: { name?: string; baseCost?: number; markupPercentage?: number }
): Promise<{ quote: Quote }> {
  const res = await apiClient.patch<{ quote: Quote }>(`/api/admin/quotes/${id}/custom-services/${componentId}`, data);
  return { quote: mapQuoteBoundary(res.quote) };
}

/**
 * Delete a custom service component (admin / operations)
 */
export async function deleteCustomService(
  id: string,
  componentId: string
): Promise<{ quote: Quote }> {
  const res = await apiClient.delete<{ quote: Quote }>(`/api/admin/quotes/${id}/custom-services/${componentId}`);
  return { quote: mapQuoteBoundary(res.quote) };
}

function mapQuoteBoundary<T extends any>(quote: T): T {
  if (!quote || typeof quote !== 'object') return quote;
  const mapped = { ...quote } as any;

  return mapped as T;
}

